'use client'
import React, { useMemo } from 'react'
import cn from 'classnames'
import { Icon } from '@/icons'

import styles from './Button.module.scss'
import Link from 'next/link'

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'ghost'

export type ButtonSize = 'large' | 'medium' | 'small'

type ButtonBaseProps = {
  className?: string
  onClick?: () => void
  name?: string
  type?: 'submit' | 'button' | 'reset'
  variant?: ButtonVariant
  size?: ButtonSize
  isNegative?: boolean
  isCircle?: boolean
  onlyIcon?: boolean
  disabled?: boolean
  isLoading?: boolean
  testId?: string
  title?: string
  children?: React.ReactNode
  ariaLabel?: string
  [key: `data-${string}`]: string | number | boolean | undefined
}

type ButtonProps = ButtonBaseProps & {
  href?: undefined
  target?: undefined
  download?: undefined
  rel?: undefined
}

type ButtonLinkProps = ButtonBaseProps & {
  href: string
  target?: '_blank' | '_self' | '_parent' | '_top' | '_unfencedTop'
  download?: boolean
  rel?: string
}

type Props = ButtonProps | ButtonLinkProps

export const Button: React.FC<Props> = (props) => {
  const {
    onClick = () => {},
    type = 'button',
    name = '',
    variant = 'primary',
    size = 'large',
    isCircle = false,
    isNegative = false,
    onlyIcon = false,
    className = '',
    testId,
    title,
    disabled = false,
    isLoading = false,
    children,
    ariaLabel,
    ...rest
  } = props

  // Оставляем только data-* атрибуты
  const dataAttributes = useMemo(() => {
    return Object.fromEntries(
      Object.entries(rest).filter(([key]) => key.startsWith('data-'))
    )
  }, [rest])

  const isDisabled = useMemo(() => {
    return disabled || isLoading
  }, [disabled, isLoading])

  const classNameButton = cn(styles.container, className, {
    [styles.isDisabled]: isDisabled,
    [styles.isCircle]: isCircle,
    [styles.isNegative]: isNegative,
    [styles.large]: size === 'large',
    [styles.medium]: size === 'medium',
    [styles.small]: size === 'small',
    [styles.primary]: variant === 'primary',
    [styles.secondary]: variant === 'secondary',
    [styles.tertiary]: variant === 'tertiary',
    [styles.ghost]: variant === 'ghost',
    [styles.onlyIcon]: onlyIcon,
  })

  if ('href' in props && props.href) {
    return (
      <Link
        href={props.href}
        className={classNameButton}
        data-testid={testId ?? 'button'}
        title={title}
        download={props.download}
        target={props.target}
        rel={props.rel}
        onClick={onClick}
        aria-label={ariaLabel}
        {...dataAttributes}
      >
        {name ? name : children}
        {isLoading ? <Icon name="loader" className={styles.loader} /> : null}
      </Link>
    )
  }
  // Обычная кнопка
  return (
    <button
      type={isDisabled ? 'button' : type}
      className={classNameButton}
      onClick={isDisabled ? undefined : onClick}
      data-testid={testId ?? 'button'}
      title={title}
      aria-label={ariaLabel}
      {...dataAttributes}
    >
      {name ? name : children}
      {isLoading ? <Icon name="loader" className={styles.loader} /> : null}
    </button>
  )
}
